import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { ToastrService } from 'ngx-toastr';
import { DatePipe } from '@angular/common';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { emailSubjectValidator } from 'src/app/private/validators/email-subject.validator';
import { CampaignService } from 'src/app/private/services/campaign.service';

@Component({
  selector: 'app-create-broadcast-campaign',
  providers: [DatePipe],
  templateUrl: './create-broadcast-campaign.component.html',
  styleUrls: ['./create-broadcast-campaign.component.scss']
})
export class CreateBroadcastCampaignComponent implements OnInit {

  @Output() close: EventEmitter<any> = new EventEmitter();
  @Output() formSubmit: EventEmitter<any> = new EventEmitter();

  @Input() type: string = 'Create';
  btnLoading = false;

  campaignForm: FormGroup;
  broadcastType: string = '';

  @Input() emailTemplates: any[] = [];
  @Input() whatsappTemplates: any[] = [];
  @Input() contactGroups: any[] = [];
  @Input() organisationEmails: any[] = [];
  @Input() campaignData: any;
  @Input() active: string = '';
  isSubmitted: boolean = false;
  isSchedule = false;
  isAdmin: boolean = false;
  totalContactsInGroup: number | null = null;
  isLoadingContactsCount: boolean = false;

  // Email Reputation
  reputationData: any = null;
  isReputationLoading: boolean = false;
  reputationError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private api: ManageCampaignService,
    private localStorage: LocalStorageService,
    private tracker: TrackingService,
    private toast: ToastrService,
    private datePipe: DatePipe,
    private contactGroupService: ContactGrouopService,
    private campaignService: CampaignService
  ) {
    this.campaignForm = this.fb.group({
      event_name: ['', Validators.required],
      from_email: [''],
      subject: [''],
      broadcast_type: ['', Validators.required],
      template_id: ['', Validators.required],
      contact_group_id: ['', Validators.required],
      status: ['Pending', Validators.required],
      is_schedule: [false],
      schedule_date: [''],
      schedule_time: ['']
    });

    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }

  ngOnInit(): void {
    // Set initial broadcast type
    this.campaignForm
      .get('broadcast_type')
      ?.setValue(this.active);

    // React to broadcast type change
    this.campaignForm
      .get('broadcast_type')
      ?.valueChanges
      .subscribe(type => {
        if (type === 'email') {
          this.applyEmailValidators();
        } else {
          this.clearEmailValidators();
        }
      });

    // Apply immediately if email
    if (this.active === 'email') {
      this.applyEmailValidators();
    }

    if (this.active === 'email' || this.campaignData?.campaign_type === 'email') {
      const fromEmail = this.resolveFromEmail();
      if (fromEmail) {
        this.campaignForm.get('from_email')?.setValue(fromEmail);
      }
    }

    if (this.type === 'Edit') {
      this.campaignForm.patchValue({
        event_name: this.campaignData.campaign_name,
        broadcast_type: this.campaignData.campaign_type,
        template_id: this.campaignData.template_id,
        contact_group_id: Number(this.campaignData.contact_group_id),
        status: this.campaignData.status,
        is_schedule: this.campaignData.is_schedule,
        schedule_date: this.campaignData.schedule_date,
        schedule_time: this.campaignData.schedule_time
      });

      this.broadcastType = this.campaignData.campaign_type;
      this.isSchedule = this.campaignData.is_schedule;
      this.active = this.campaignData.campaign_type;

      if (this.broadcastType == 'email') {
        this.campaignForm.patchValue({
          from_email: this.campaignData.from_email,
          subject: this.campaignData.subject,
        });
      }
    }

    // Subscribe to contact group changes
    this.campaignForm.get('contact_group_id')?.valueChanges.subscribe(groupId => {
      if (groupId) {
        this.getContactCount(groupId);
      } else {
        this.totalContactsInGroup = null;
      }
    });

    // Auto-fetch reputation when from_email changes (email campaigns only)
    this.campaignForm.get('from_email')?.valueChanges.subscribe((email: string) => {
      if (this.active === 'email' && email) {
        this.fetchEmailReputation(email);
      } else {
        this.reputationData = null;
        this.reputationError = null;
      }
    });

    // Also fetch on init if from_email is already set (default / edit mode)
    setTimeout(() => {
      if (this.active === 'email') {
        const currentEmail = this.campaignForm.get('from_email')?.value;
        if (currentEmail) {
          this.fetchEmailReputation(currentEmail);
        }
      }
    }, 0);

    if (this.campaignForm.get('contact_group_id')?.value) {
      this.getContactCount(this.campaignForm.get('contact_group_id')?.value);
    }

    setTimeout(() => {
      if (this.campaignData?.is_schedule) {
        if (this.campaignData.schedule_date) {
          const scheduleDate = new Date(this.campaignData.schedule_date);
          if (!isNaN(scheduleDate.getTime())) {
            this.campaignForm.get('schedule_date')?.setValue(scheduleDate);
          }
        }

        if (this.campaignData.schedule_time) {
          const today = new Date();
          const timeParts = this.campaignData.schedule_time.split(':');
          if (timeParts.length >= 2) {
            const scheduleTime = new Date(today);
            scheduleTime.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);
            if (!isNaN(scheduleTime.getTime())) {
              this.campaignForm.get('schedule_time')?.setValue(scheduleTime);
            }
          }
        }
      }
    }, 500);

    // Filter templates with empty template_name
    this.whatsappTemplates = this.whatsappTemplates.filter(
      template => (template.template_name !== '' && template.template_name !== null)
    );
  }

  getOrgEmail() {
    if (!this.organisationEmails?.length) {
      return [];
    }
    return this.organisationEmails.filter((item: any) => item?.type === 'campaign');
  }

  ngAfterViewInit() {}

  onBroadcastTypeChange(event: any) {}

  private applyEmailValidators() {
    const fromEmailCtrl: any = this.campaignForm.get('from_email');
    const subjectCtrl = this.campaignForm.get('subject');

    fromEmailCtrl?.setValidators([Validators.required]);
    subjectCtrl?.setValidators([Validators.required, emailSubjectValidator, Validators.minLength(5), Validators.maxLength(100)]);

    fromEmailCtrl?.updateValueAndValidity();
    subjectCtrl?.updateValueAndValidity();

    if (!fromEmailCtrl?.value) {
      const fromEmail = this.resolveFromEmail();
      if (fromEmail) {
        fromEmailCtrl.setValue(fromEmail);
      }
    }
  }

  private clearEmailValidators() {
    const fromEmailCtrl = this.campaignForm.get('from_email');
    const subjectCtrl = this.campaignForm.get('subject');

    fromEmailCtrl?.clearValidators();
    subjectCtrl?.clearValidators();

    fromEmailCtrl?.updateValueAndValidity();
    subjectCtrl?.updateValueAndValidity();
  }

  private resolveFromEmail(): string | null {
    if (!this.organisationEmails?.length) {
      return null;
    }
    const campaignEmails = this.organisationEmails.filter((item: any) => item?.type === 'campaign');
    if (!campaignEmails.length) {
      return null;
    }
    return campaignEmails[0]?.email || campaignEmails[0];
  }

  formatToUTC(date: Date, time?: string): string {
    const d = new Date(date);
    if (time) {
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return (
      d.getUTCFullYear() + '-' +
      String(d.getUTCMonth() + 1).padStart(2, '0') + '-' +
      String(d.getUTCDate()).padStart(2, '0') + ' ' +
      String(d.getUTCHours()).padStart(2, '0') + ':' +
      String(d.getUTCMinutes()).padStart(2, '0') + ':' +
      String(d.getUTCSeconds()).padStart(2, '0') +
      '+00'
    );
  }

  onSubmit() {
    this.isSubmitted = true;
    let formattedTime: any = null;
    let formattedDate: any = null;

    if (this.campaignForm.valid) {
      if (this.isSchedule) {
        const scheduleDate = this.campaignForm.get('schedule_date')?.value;
        const scheduleTime = this.campaignForm.get('schedule_time')?.value;

        if (scheduleTime) {
          const [hours, minutes] = scheduleTime.split(':');
          const timeAsDate = new Date();
          timeAsDate.setHours(+hours, +minutes, 0, 0);
          formattedTime = this.datePipe.transform(timeAsDate, 'HH:mm');
        }
        formattedDate = this.formatToUTC(scheduleDate, formattedTime);
      }

      let status = this.campaignForm.get('status')?.value;

      let payload: any = {
        campaign_name: this.campaignForm.get('event_name')?.value,
        campaign_type: this.campaignForm.get('broadcast_type')?.value,
        template_id: this.campaignForm.get('template_id')?.value,
        subject: this.campaignForm.get('subject')?.value,
        hospital_id: this.localStorage.getHospitalId(),
        contact_group_id: this.campaignForm.get('contact_group_id')?.value,
        is_schedule: this.isSchedule,
        schedule_date: formattedDate,
        schedule_time: formattedTime,
        status: status
      };

      if (this.campaignForm.get('broadcast_type')?.value == 'email') {
        payload.from_email = this.campaignForm.get('from_email')?.value;
      }

      if (!this.isAdmin) {
        payload['user_id'] = this.localStorage.getUserId();
      }

      this.btnLoading = true;
      try {
        if (this.type === 'Create') {
          this.api.createCampaign(payload).subscribe({
            next: (res: any) => {
              const desc = `Broadcast campaign created with name: ${payload.campaign_name}, type: ${payload.campaign_type}`;
              this.tracker.track('Create', 'Broadcast Campaign', desc);
              this.formSubmit.emit();
              this.btnLoading = false;
            },
            error: (error: any) => {
              console.error('Error creating campaign:', error);
              this.toast.error('Failed to create broadcast campaign. Please try again.', 'Error');
              this.btnLoading = false;
            }
          });
        } else {
          const payload2 = {
            id: this.campaignData.id,
            ...payload
          };
          this.api.updateCampaign(payload2).subscribe({
            next: (res: any) => {
              const desc = `Broadcast campaign updated with ID: ${payload2.id}, name: ${payload2.campaign_name}, and type: ${payload2.campaign_type}`;
              this.tracker.track('Update', 'Broadcast Campaign', desc);
              this.toast.success('Campaign updated successfully!', 'Success');
              this.formSubmit.emit();
              this.btnLoading = false;
            },
            error: (error: any) => {
              console.error('Error updating campaign:', error);
              this.toast.error('Failed to update campaign. Please try again.', 'Error');
              this.btnLoading = false;
            }
          });
        }
      } catch (e: any) {
        this.btnLoading = false;
      }
    }
  }

  onScheduleChange(event: any) {
    this.isSchedule = event.checked;
    if (this.isSchedule) {
      this.campaignForm.get('schedule_date')?.setValidators(Validators.required);
      this.campaignForm.get('schedule_time')?.setValidators(Validators.required);
    } else {
      this.campaignForm.get('schedule_date')?.clearValidators();
      this.campaignForm.get('schedule_time')?.clearValidators();
    }
    this.campaignForm.get('schedule_date')?.updateValueAndValidity();
    this.campaignForm.get('schedule_time')?.updateValueAndValidity();
  }

  closeModal() {
    this.close.emit();
  }

  getContactCount(groupId: number) {
    this.isLoadingContactsCount = true;
    const payload = {
      where: {
        hospital_id: this.localStorage.getHospitalId(),
        contact_group_id: groupId
      },
      filter: null,
      page: 0,
      limit: 10,
      order: {
        col: '"contact_group_list"."updatedAt"',
        dir: 'DESC'
      }
    };

    this.contactGroupService.getAllContactGroupList(payload).subscribe({
      next: (res: any) => {
        if (res && res.count !== undefined) {
          this.totalContactsInGroup = res.count;
        } else {
          this.totalContactsInGroup = 0;
        }
        this.isLoadingContactsCount = false;
      },
      error: (err: any) => {
        console.error('Error fetching contact count:', err);
        this.totalContactsInGroup = null;
        this.isLoadingContactsCount = false;
      }
    });
  }

  fetchEmailReputation(emailId: string) {
    this.isReputationLoading = true;
    this.reputationData = null;
    this.reputationError = null;
    this.campaignService.getReputation({
      email_id: emailId,
      hospital_id: this.localStorage.getHospitalId()
    }).subscribe({
      next: (res: any) => {
        if (res.success == true) {
          this.reputationData = res.data;
        } else {
          this.reputationData = null;
        }
        this.isReputationLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching email reputation:', err);
        this.reputationError = 'Unable to load email health data.';
        this.isReputationLoading = false;
      }
    });
  }

  getReputationScoreColor(score: number): string {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  }

  getReputationScoreLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Fair';
    return 'Poor';
  }

  getReputationScoreBg(score: number): string {
    if (score >= 80) return 'rgba(16,185,129,0.1)';
    if (score >= 60) return 'rgba(245,158,11,0.1)';
    return 'rgba(239,68,68,0.1)';
  }

  getRiskColor(riskLevel: string): string {
    const level = (riskLevel || '').toLowerCase();
    if (level === 'low') return '#10b981';
    if (level === 'medium') return '#f59e0b';
    return '#ef4444';
  }

  getRiskBg(riskLevel: string): string {
    const level = (riskLevel || '').toLowerCase();
    if (level === 'low') return 'rgba(16,185,129,0.12)';
    if (level === 'medium') return 'rgba(245,158,11,0.12)';
    return 'rgba(239,68,68,0.12)';
  }

  getWarmupPercent(warmup: any): number {
    if (!warmup || !warmup.daily_limit) return 0;
    return Math.round((warmup.sent_today / warmup.daily_limit) * 100);
  }
}
